/**
 * Error responses in our words. PocketBase answers in its own English ("Failed to authenticate.",
 * "Cannot be blank."), which reads foreign in the UI and names the backend to anyone who knows
 * it. Every error response passes through `localizeErrorBody` (the SDK's afterSend,
 * lib/core/pocketbase.ts) before it becomes a ClientResponseError, so toasts, error states and
 * field errors show our wording in the UI language. Our hooks' own messages pass unchanged.
 */
import i18n from "@/i18n";

type FieldError = { code?: string; message?: string; params?: Record<string, unknown> };

/** PocketBase's stock messages (0.40) -> ours. Server errors (5xx) always become "failed". */
const STOCK_MESSAGES: Record<string, string> = {
  "Failed to authenticate.": "serverErrors.stock.authFailed",
  "The request doesn't satisfy the collection requirements to authenticate.":
    "serverErrors.stock.notVerified",
  "An error occurred while validating the submitted data.": "serverErrors.stock.invalid",
  "The requested resource wasn't found.": "serverErrors.stock.notFound",
  "Missing collection context.": "serverErrors.stock.notFound",
  "File not found.": "serverErrors.stock.notFound",
  "The request requires valid record authorization token.": "serverErrors.stock.signedOut",
  "Only superusers can perform this action.": "serverErrors.stock.forbidden",
  "The authorized record is not allowed to perform this action.": "serverErrors.stock.forbidden",
  "Failed to create record.": "serverErrors.stock.saveFailed",
  "Failed to update record.": "serverErrors.stock.saveFailed",
  "Failed to delete record.": "serverErrors.stock.failed",
  "Something went wrong while processing your request.": "serverErrors.stock.failed",
  "Too Many Requests.": "serverErrors.rate_limited",
};

/**
 * Our wording, in the UI language, for an error code: PocketBase's validation codes and our
 * hooks' (server/pb_hooks). Null when we have none (the server's message stays).
 */
export function translatedCode(code: string | undefined, params?: object): string | null {
  if (!code) return null;
  const key = `serverErrors.${code.startsWith("validation_pow_") ? "validation_pow" : code}`;
  return i18n.exists(key) ? String(i18n.t(key as never, params as never)) : null;
}

/** An error response's body with PocketBase's own wording replaced by ours. */
export function localizeErrorBody(status: number, body: unknown): unknown {
  if (!body || typeof body !== "object") return body;
  const { message, data } = body as { message?: unknown; data?: unknown };
  const stock = status >= 500 ? "serverErrors.stock.failed" : STOCK_MESSAGES[String(message)];
  const fields =
    data && typeof data === "object"
      ? Object.fromEntries(
          Object.entries(data).map(([name, error]) => {
            const field = error as FieldError | null;
            const text = translatedCode(field?.code, field?.params);
            return [name, text ? { ...field, message: text } : error];
          }),
        )
      : data;
  return { ...body, message: stock ? i18n.t(stock as never) : message, data: fields };
}
